import { BracketMatch, LabeledGirone, PartialPair } from "../api/Tournaments";
import { PadelScore } from "../types/Result";
import { AmericanoStanding, StandingsCriteria } from "../types/Tournaments";

// Punti in palio quando un pareggio si decide ai calci di rigore.
export type ShootoutPoints = { win: number; lose: number };

// Punti assegnati da una partita finita in pareggio e decisa ai rigori.
// Si preferiscono quelli registrati sulla partita (esatti anche se le
// impostazioni del torneo cambiano dopo che è stata giocata).
export const shootoutAward = (
  match: BracketMatch,
  shootoutPoints?: ShootoutPoints
): PadelScore | undefined => {
  const s = match.shootout;
  if (!s || s.team1 === s.team2) return undefined;
  if (match.shootoutPoints) return match.shootoutPoints;
  if (!shootoutPoints) return undefined;
  const homeWon = s.team1 > s.team2;
  return {
    team1: homeWon ? shootoutPoints.win : shootoutPoints.lose,
    team2: homeWon ? shootoutPoints.lose : shootoutPoints.win,
  };
};

// Classifica dei gironi condivisa (allineata al backend, tournament.service.ts).
// - "difference": punti → differenza reti/set → secondario (reti fatte / diff game).
// - "headToHead": punti → scontri diretti (classifica avulsa fra chi è a pari
//   punti) → differenza complessiva come ultimo spareggio.

const points = (p: PartialPair) => p.rank || 0;

const overallDiff = (p: PartialPair, isFootball: boolean) =>
  isFootball
    ? (p.goalsFor || 0) - (p.goalsAgainst || 0)
    : (p.setsWon || 0) - (p.setsLost || 0);

const overallSecondary = (p: PartialPair, isFootball: boolean) =>
  isFootball
    ? p.goalsFor || 0
    : (p.gamesWon || 0) - (p.gamesLost || 0);

const compareByDifference =
  (isFootball: boolean) => (a: PartialPair, b: PartialPair) => {
    const d = overallDiff(b, isFootball) - overallDiff(a, isFootball);
    if (d !== 0) return d;
    return overallSecondary(b, isFootball) - overallSecondary(a, isFootball);
  };

// Ordina un gruppo a pari punti secondo gli scontri diretti (avulsa).
const orderGroupHeadToHead = (
  group: PartialPair[],
  calendario: LabeledGirone["calendario"],
  isFootball: boolean,
  shootoutPoints?: ShootoutPoints
): PartialPair[] => {
  const ids = new Set(group.map((p) => p.id));
  const mini = new Map<string, { pts: number; diff: number; sec: number }>();
  group.forEach((p) => mini.set(p.id, { pts: 0, diff: 0, sec: 0 }));

  for (const day of calendario || []) {
    for (const m of day.matches || []) {
      const home = m.home;
      const away = m.away;
      if (typeof home === "string" || typeof away === "string") continue;
      if (!home?.id || !away?.id) continue;
      if (!ids.has(home.id) || !ids.has(away.id)) continue;
      const score = m.score || [];
      if (!score.length) continue; // partita non ancora giocata

      let homeSets = 0;
      let awaySets = 0;
      let homeFor = 0;
      let awayFor = 0;
      score.forEach((s) => {
        if (s.team1 > s.team2) homeSets++;
        else if (s.team2 > s.team1) awaySets++;
        homeFor += s.team1 || 0;
        awayFor += s.team2 || 0;
      });

      const H = mini.get(home.id)!;
      const A = mini.get(away.id)!;
      if (isFootball) {
        // Pareggio deciso ai rigori: valgono i punti configurati (i gol dei
        // rigori non entrano nella differenza reti).
        const award = shootoutAward(m, shootoutPoints);
        if (homeFor > awayFor) H.pts += 3;
        else if (awayFor > homeFor) A.pts += 3;
        else if (award) {
          H.pts += award.team1;
          A.pts += award.team2;
        } else {
          H.pts += 1;
          A.pts += 1;
        }
        H.diff += homeFor - awayFor;
        A.diff += awayFor - homeFor;
        H.sec += homeFor;
        A.sec += awayFor;
      } else {
        if (homeSets > awaySets) H.pts += 3;
        else if (awaySets > homeSets) A.pts += 3;
        H.diff += homeSets - awaySets; // differenza set
        A.diff += awaySets - homeSets;
        H.sec += homeFor - awayFor; // differenza game
        A.sec += awayFor - homeFor;
      }
    }
  }

  const fallback = compareByDifference(isFootball);
  return [...group].sort((a, b) => {
    const ma = mini.get(a.id)!;
    const mb = mini.get(b.id)!;
    if (mb.pts !== ma.pts) return mb.pts - ma.pts;
    if (mb.diff !== ma.diff) return mb.diff - ma.diff;
    if (mb.sec !== ma.sec) return mb.sec - ma.sec;
    return fallback(a, b);
  });
};

export const computeGironeStandings = (
  girone: LabeledGirone,
  criteria: StandingsCriteria = "difference",
  isFootball = false,
  shootoutPoints?: ShootoutPoints
): PartialPair[] => {
  const pairs = [...(girone.pairs || [])];

  if (criteria !== "headToHead") {
    const byDiff = compareByDifference(isFootball);
    return pairs.sort((a, b) => {
      const pd = points(b) - points(a);
      if (pd !== 0) return pd;
      return byDiff(a, b);
    });
  }

  const byPoints = pairs.sort((a, b) => points(b) - points(a));
  const calendario = girone.calendario || [];
  const result: PartialPair[] = [];
  let i = 0;
  while (i < byPoints.length) {
    let j = i;
    while (j < byPoints.length && points(byPoints[j]) === points(byPoints[i]))
      j++;
    const group = byPoints.slice(i, j);
    if (group.length <= 1) result.push(...group);
    else
      result.push(
        ...orderGroupHeadToHead(group, calendario, isFootball, shootoutPoints)
      );
    i = j;
  }
  return result;
};

// Classifica di un torneo Americano (per singolo giocatore), calcolata dai
// punteggi grezzi delle partite. Allineata al backend (computeAmericanoStandings
// in tournament.service.ts): ogni giocatore accumula i punti vinti dal proprio
// team; ordinamento per punti, poi "difference" (differenza) o "headToHead"
// (scontro diretto fra i pari punti). Default: "headToHead".
export const computeAmericanoStandings = (
  girone: LabeledGirone,
  criteria: StandingsCriteria = "headToHead"
): AmericanoStanding[] => {
  const stats = new Map<string, AmericanoStanding>();
  const ensure = (id: string, name: string) => {
    let s = stats.get(id);
    if (!s) {
      s = { id, name, points: 0, conceded: 0, diff: 0, played: 0 };
      stats.set(id, s);
    }
    return s;
  };

  for (const p of girone.pairs || []) {
    if (p.id === "BYE") continue;
    ensure(p.id, p.player1Name);
  }

  const h2h = new Map<string, number>();
  const bump = (id: string, opp: string, delta: number) =>
    h2h.set(`${id}|${opp}`, (h2h.get(`${id}|${opp}`) || 0) + delta);

  const teamPlayers = (
    t: PartialPair | "BYE"
  ): { id: string; name: string }[] => {
    if (t === "BYE" || (t as PartialPair).id === "BYE") return [];
    const p = t as PartialPair;
    const out: { id: string; name: string }[] = [];
    if (p.player1Id) out.push({ id: p.player1Id, name: p.player1Name });
    if (p.player2Id) out.push({ id: p.player2Id, name: p.player2Name });
    return out;
  };

  for (const day of girone.calendario || []) {
    for (const m of day.matches || []) {
      const score = m.score || [];
      if (!score.length) continue;
      const homePts = score[0].team1 || 0;
      const awayPts = score[0].team2 || 0;
      const home = teamPlayers(m.home);
      const away = teamPlayers(m.away);
      if (!home.length || !away.length) continue;

      for (const p of home) {
        const s = ensure(p.id, p.name);
        s.points += homePts;
        s.conceded += awayPts;
        s.diff += homePts - awayPts;
        s.played += 1;
        for (const o of away) bump(p.id, o.id, homePts - awayPts);
      }
      for (const p of away) {
        const s = ensure(p.id, p.name);
        s.points += awayPts;
        s.conceded += homePts;
        s.diff += awayPts - homePts;
        s.played += 1;
        for (const o of home) bump(p.id, o.id, awayPts - homePts);
      }
    }
  }

  const rows = Array.from(stats.values());

  if (criteria !== "headToHead") {
    return rows.sort((a, b) => b.points - a.points || b.diff - a.diff);
  }

  const byPoints = Array.from(rows).sort((a, b) => b.points - a.points);
  const result: AmericanoStanding[] = [];
  let i = 0;
  while (i < byPoints.length) {
    let j = i;
    while (j < byPoints.length && byPoints[j].points === byPoints[i].points) j++;
    const group = byPoints.slice(i, j);
    if (group.length <= 1) {
      result.push(...group);
    } else {
      const h2hDiff = (id: string) =>
        group.reduce(
          (acc, o) =>
            o.id === id ? acc : acc + (h2h.get(`${id}|${o.id}`) || 0),
          0
        );
      result.push(
        ...group.sort((a, b) => h2hDiff(b.id) - h2hDiff(a.id) || b.diff - a.diff)
      );
    }
    i = j;
  }
  return result;
};
